import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CustomTooltip } from '../Poppers/CustomTooltip';
import Icons from '../Icons';
import { Themes } from '../../style/theme-consts';
import * as S from './styled';
import { useIsTruncated } from './useIsTruncated';

const useCopyToClipboard = () => {
  const [isCopied, setIsCopied] = useState(false);

  const copy = useCallback(text => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2500);
        })
        .catch(() => setIsCopied(false));
    }
  }, []);

  return { isCopied, copy };
};

const SmartText = ({
  children,
  lineClamp = 1,
  maxWidth = '300px',
  tooltipMode,
  tooltipPlacement = 'top',
  tooltipProps,
  tooltipTitle,
  copyable = false,
  valueToCopy,
  className
}) => {
  const { isTruncated, textRef } = useIsTruncated(children);
  const { isCopied, copy } = useCopyToClipboard();

  const handleCopy = e => {
    e.preventDefault();
    e.stopPropagation();
    const text = valueToCopy || (typeof children === 'string' ? children : '');
    copy(text);
  };

  const textElement = (
    <S.Text ref={textRef} $lineClamp={lineClamp} className={className}>
      {children}
    </S.Text>
  );

  const resolvedTooltipTitle = tooltipTitle !== undefined ? tooltipTitle : children;

  const textWithTooltip =
    isTruncated || tooltipTitle !== undefined ? (
      <CustomTooltip
        title={resolvedTooltipTitle}
        placement={tooltipPlacement}
        mode={tooltipMode}
        width="auto"
        maxWidth={maxWidth}
        {...tooltipProps}>
        {textElement}
      </CustomTooltip>
    ) : (
      textElement
    );

  if (!copyable) return textWithTooltip;

  const copyTooltipText = isCopied ? 'Copied!' : 'Copy';
  const copyTheme = tooltipMode || Themes.LIGHT;

  return (
    <S.Wrapper>
      {textWithTooltip}
      <CustomTooltip mode={copyTheme} title={copyTooltipText} arrow placement="top" leaveDelay={300}>
        <S.CopyIcon>{isCopied ? <Icons.DoneAll /> : <Icons.Copy onClick={handleCopy} />}</S.CopyIcon>
      </CustomTooltip>
    </S.Wrapper>
  );
};

SmartText.propTypes = {
  children: PropTypes.node.isRequired,
  lineClamp: PropTypes.number,
  maxWidth: PropTypes.string,
  tooltipMode: PropTypes.oneOf(['dark-mode', 'light-mode']),
  tooltipPlacement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top'
  ]),
  tooltipProps: PropTypes.shape({}),
  tooltipTitle: PropTypes.node,
  copyable: PropTypes.bool,
  valueToCopy: PropTypes.string,
  className: PropTypes.string
};

export { SmartText };
